import React, { useEffect, useMemo, useState } from 'react'
import { Tour, TourProps } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { updateTourProgress } from '../../../store/session/action-creators'
import { TOURS_ENABLED } from '../tours-enabled'

const CARD_TOUR_ID = 'tour.appeals.card.v1'

function safeTarget(getter: () => HTMLElement | null): () => HTMLElement {
  return () => getter() || document.body
}

function getFormTarget(): HTMLElement | null {
  return document.querySelector('[data-tour-id="appeal-form"]') as HTMLElement | null
}

function getStageTarget(): HTMLElement | null {
  return document.querySelector('[data-tour-id="appeal-stage-steps"]') as HTMLElement | null
}

function getActionsTarget(): HTMLElement | null {
  return document.querySelector('[data-tour-id="appeal-card-actions"]') as HTMLElement | null
}

function getTabsTarget(): HTMLElement | null {
  const root = document.querySelector('[data-tour-id="appeal-tabs"]') as HTMLElement | null
  if (!root) return null
  return (root.querySelector('.ant-tabs-nav') as HTMLElement | null) || root
}

export function AppealCardOnboardingTour() {
  const dispatch = useDispatch()
  const sessionData = useSelector((s: any) => s.session?.sessionData)
  const tours = (sessionData as any)?.jsonSettings?.tours || {}
  const progress = tours?.[CARD_TOUR_ID] || {}

  const completed = Boolean(progress?.completed)
  const shouldOpen = Boolean(progress?.stage === 'prompted' || progress?.stage === 'in_progress')
  const showCongrats = Boolean(progress?.showCongrats)

  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<number>(Number(progress?.currentStep) || 0)

  useEffect(() => {
    if (completed) return
    if (!shouldOpen) return
    setOpen(true)
  }, [completed, shouldOpen])

  useEffect(() => {
    const stepFromStore = Number(progress?.currentStep) || 0
    if (!Number.isNaN(stepFromStore) && stepFromStore !== current) {
      setCurrent(stepFromStore)
    }
  }, [progress?.currentStep])

  const steps: TourProps['steps'] = useMemo(() => {
    const base: TourProps['steps'] = [
      {
        title: 'Карточка обращения',
        description:
          'Это карточка обращения: здесь вы заполняете данные клиента и обращения, прикрепляете файлы и ведёте историю — всё в одном месте.',
        target: safeTarget(getFormTarget),
      },
      {
        title: 'Стадии обработки',
        description:
          'Стадии показывают текущий этап работы по обращению. По мере обработки вы будете продвигать обращение дальше — так команде проще контролировать статус.',
        target: safeTarget(getStageTarget),
      },
      {
        title: 'Управление карточкой',
        description:
          'Здесь собраны основные действия: переход по стадиям, добавление заметок, создание задач и другие операции — в зависимости от прав и текущей стадии.',
        target: safeTarget(getActionsTarget),
      },
      {
        title: 'Табы карточки',
        description:
          'Карточка разделена на вкладки:\n' +
          '— «Данные заказа»: основные поля и реквизиты\n' +
          '— «Состав заказа»: позиции (если доступно)\n' +
          '— «Лента событий»: история изменений и прогноз\n' +
          '— «Чаты»: коммуникация\n' +
          '— «Прикрепленные файлы»: документы\n' +
          '— «Связи»: связанные сущности',
        target: safeTarget(getTabsTarget),
      },
    ]

    if (!showCongrats) return base

    return [
      {
        title: 'Отлично!',
        description:
          'Поздравляем: ваше первое обращение успешно создано и сохранено. Дальше покажу, как устроена карточка и где находятся ключевые элементы управления.',
        target: () => null,
        placement: 'center',
      },
      ...base,
    ]
  }, [showCongrats])

  const onClose = async () => {
    setOpen(false)
    await dispatch(
      updateTourProgress(CARD_TOUR_ID, { stage: 'closed', currentStep: current }) as any,
    )
  }

  const onFinish = async () => {
    setOpen(false)
    await dispatch(
      updateTourProgress(CARD_TOUR_ID, {
        stage: 'completed',
        completed: true,
        currentStep: steps.length - 1,
      }) as any,
    )
  }

  const onChange = async (next: number) => {
    const clamped = Math.max(0, Math.min(next, steps.length - 1))
    setCurrent(clamped)
    await dispatch(
      updateTourProgress(CARD_TOUR_ID, {
        stage: 'in_progress',
        currentStep: clamped,
        ...(showCongrats && clamped > 0 ? { showCongrats: false } : {}),
      }) as any,
    )
  }

  if (!TOURS_ENABLED || completed) return null

  return (
    <Tour
      open={open}
      current={current}
      onClose={onClose}
      onFinish={onFinish}
      onChange={onChange}
      steps={steps}
    />
  )
}
